import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Save, Search } from 'lucide-react'
import { terminologyApi, type TermMap } from '@/api/terminologyApi'
import { NAMESPACES, type Namespace } from '@/i18n/termKeys'
import i18n from '@/i18n/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

/** {{token}} placeholders in a value, for preservation checks. */
function placeholders(value: string): string[] {
  return [...value.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)].map((m) => m[1])
}

/**
 * Term editor for a single locale. Controlled via the `locale` prop (the dialog that
 * opens it owns the selection). Namespace tabs + search; each row shows the base value
 * (placeholder) vs the override input. Validates (non-empty + preserved placeholders),
 * saves via PUT, and reloads i18next so labels update live.
 */
export function TerminologyEditor({ locale }: { locale: string }) {
  const queryClient = useQueryClient()

  const [namespace, setNamespace] = useState<Namespace>(NAMESPACES[0])
  const [query, setQuery] = useState('')

  // Resolve the selected locale's immediate parent (its fallback) so the
  // "base" column reflects the actual inherited value, not en-standard. For
  // chains like es-clinical -> es-standard -> en-standard, the editor should
  // compare against es-standard.
  const profilesQuery = useQuery({
    queryKey: ['terms', 'profiles'],
    queryFn: terminologyApi.listProfiles,
  })
  const parentLocale = useMemo(() => {
    const profile = profilesQuery.data?.find((p) => p.id === locale)
    return profile?.fallback ?? null
  }, [profilesQuery.data, locale])

  const localeTerms = useQuery({
    queryKey: ['terms', locale, namespace],
    queryFn: () => terminologyApi.getNamespace(locale, namespace),
  })
  const baseTerms = useQuery({
    queryKey: ['terms', parentLocale ?? locale, namespace],
    queryFn: () =>
      parentLocale
        ? terminologyApi.getNamespace(parentLocale, namespace)
        : Promise.resolve<TermMap>({}),
    enabled: parentLocale !== undefined,
  })

  // Local edits keyed by term key; absent keys fall back to the fetched value.
  const [edits, setEdits] = useState<TermMap>({})
  // Brand-new keys added in this session (rendered as rows before they're saved).
  const [addedKeys, setAddedKeys] = useState<string[]>([])
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [saved, setSaved] = useState(false)

  // Reset local state whenever the locale/namespace selection changes.
  const selectionKey = `${locale}:${namespace}`
  const [lastSelection, setLastSelection] = useState(selectionKey)
  if (selectionKey !== lastSelection) {
    setLastSelection(selectionKey)
    setEdits({})
    setAddedKeys([])
    setNewKey('')
    setNewValue('')
    setErrors([])
    setSaved(false)
    setQuery('')
  }

  const keys = useMemo(() => {
    // Saved keys plus any not-yet-saved keys the admin just added.
    const all = Array.from(
      new Set([...Object.keys(localeTerms.data ?? {}), ...addedKeys]),
    ).sort()
    const q = query.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (k) =>
        k.toLowerCase().includes(q) ||
        (localeTerms.data?.[k] ?? '').toLowerCase().includes(q) ||
        (baseTerms.data?.[k] ?? '').toLowerCase().includes(q),
    )
  }, [localeTerms.data, baseTerms.data, addedKeys, query])

  const save = useMutation({
    mutationFn: (values: TermMap) =>
      terminologyApi.saveOverrides(locale, namespace, values as never),
    onSuccess: async () => {
      setSaved(true)
      setErrors([])
      setEdits({})
      setAddedKeys([])
      await i18n.reloadResources(locale, namespace)
      await queryClient.invalidateQueries({ queryKey: ['terms', locale, namespace] })
    },
    onError: (err) => {
      setSaved(false)
      setErrors([err instanceof Error ? err.message : 'Save failed'])
    },
  })

  function valueFor(key: string): string {
    return edits[key] ?? localeTerms.data?.[key] ?? ''
  }

  function validate(values: TermMap): string[] {
    const found: string[] = []
    for (const [key, value] of Object.entries(values)) {
      if (value.trim() === '') {
        found.push(`"${key}" cannot be empty`)
        continue
      }
      const baseValue = baseTerms.data?.[key]
      if (baseValue) {
        for (const token of placeholders(baseValue)) {
          if (!placeholders(value).includes(token)) {
            found.push(`"${key}" must keep the {{${token}}} placeholder`)
          }
        }
      }
    }
    return found
  }

  function handleSave() {
    // Only send keys the admin actually changed.
    const changed: TermMap = {}
    for (const key of Object.keys(edits)) {
      if (edits[key] !== localeTerms.data?.[key]) changed[key] = edits[key]
    }
    if (Object.keys(changed).length === 0) {
      setErrors(['No changes to save'])
      return
    }
    const validationErrors = validate(changed)
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }
    save.mutate(changed)
  }

  function handleAddTerm() {
    const key = newKey.trim()
    if (!key) {
      setErrors(['Enter a key for the new term'])
      return
    }
    if (/[:\s]/.test(key)) {
      setErrors([`Key "${key}" cannot contain spaces or ":"`])
      return
    }
    if (key in (localeTerms.data ?? {}) || addedKeys.includes(key)) {
      setErrors([`"${key}" already exists`])
      return
    }
    if (!newValue.trim()) {
      setErrors(['Enter a value for the new term'])
      return
    }
    setErrors([])
    setSaved(false)
    setAddedKeys((prev) => [...prev, key])
    setEdits((prev) => ({ ...prev, [key]: newValue }))
    setNewKey('')
    setNewValue('')
  }

  const loading = localeTerms.isLoading || baseTerms.isLoading
  const dirtyCount = Object.keys(edits).filter(
    (k) => edits[k] !== localeTerms.data?.[k],
  ).length

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <Tabs value={namespace} onValueChange={(v) => setNamespace(v as Namespace)}>
        <TabsList className="flex flex-wrap h-auto">
          {NAMESPACES.map((ns) => (
            <TabsTrigger key={ns} value={ns} className="text-xs">
              {ns}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search terms…"
          className="h-9 pl-8"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Add a brand-new term (key + value) to this namespace. */}
      <form
        className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-2"
        onSubmit={(e) => {
          e.preventDefault()
          handleAddTerm()
        }}
      >
        <Input
          placeholder="new_key"
          className="h-9 w-40 font-mono text-xs"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
        />
        <Input
          placeholder="Value"
          className="h-9 flex-1"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
        />
        <Button type="submit" variant="outline" size="sm">
          <Plus className="mr-1.5 h-4 w-4" />
          Add term
        </Button>
      </form>

      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t save</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      {saved && (
        <Alert>
          <AlertTitle>Saved</AlertTitle>
          <AlertDescription>Terminology updated and reloaded.</AlertDescription>
        </Alert>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading terms…</p>
        ) : keys.length === 0 ? (
          <p className="text-sm text-muted-foreground">No terms match “{query}”.</p>
        ) : (
          <div className="flex flex-col divide-y">
            {keys.map((key) => {
              const isDirty = edits[key] !== undefined && edits[key] !== localeTerms.data?.[key]
              return (
                <div
                  key={key}
                  className="grid grid-cols-[10rem_1fr] items-center gap-3 py-2"
                >
                  <div className="flex flex-col">
                    <span className="font-mono text-xs text-foreground">{key}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      base: {baseTerms.data?.[key] ?? '—'}
                    </span>
                  </div>
                  <Input
                    value={valueFor(key)}
                    placeholder={baseTerms.data?.[key]}
                    className={isDirty ? 'border-primary' : undefined}
                    onChange={(e) => {
                      setSaved(false)
                      setEdits((prev) => ({ ...prev, [key]: e.target.value }))
                    }}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t pt-3">
        <span className="text-xs text-muted-foreground">
          {dirtyCount > 0 ? `${dirtyCount} unsaved change${dirtyCount > 1 ? 's' : ''}` : 'No changes'}
        </span>
        <Button onClick={handleSave} disabled={save.isPending || dirtyCount === 0}>
          <Save className="mr-2 h-4 w-4" />
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}
